import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import { ReadCallPlanResponse } from 'features/care-connect-settings/domain/models/call-plan-response';
import { UpsertCallPlanCommand } from 'features/care-connect-settings/domain/models/upsert-call-plan-command';
import { UpsertCallPlanResponse } from 'features/care-connect-settings/domain/models/upsert-call-plan-response';
import { SubscribableOrganisationUnitTreeQueryPaths } from 'features/care-connect-settings/domain/queries/subscribable-organisation-unit-tree-queries';

const callPlansBaseUrl = '/api/v1/CallPlans';
const callPlansBaseUrlV2 = '/api/v2/CallPlans';

enum CallPlanQueryPaths {
  ReadCallPlan = 'ReadCallPlan',
  UpsertCallPlan = 'UpsertCallPlan',
  RemoveCallPlan = 'RemoveCallPlan',
}

const readCallPlan = async (axiosInstance: AxiosInstance, organisationUnitId: string): Promise<ReadCallPlanResponse | null> => {
  try {
    const response = await axiosInstance.get(`${callPlansBaseUrl}/${CallPlanQueryPaths.ReadCallPlan}/${organisationUnitId}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 400) {
      return null;
    }
    throw error;
  }
};

const upsertCallPlan = async (axiosInstance: AxiosInstance, command: UpsertCallPlanCommand): Promise<UpsertCallPlanResponse> => {
  try {
    const response = await axiosInstance.post<UpsertCallPlanResponse>(
      `${callPlansBaseUrlV2}/${CallPlanQueryPaths.UpsertCallPlan}`,
      command,
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 400) {
      return error.response.data as UpsertCallPlanResponse;
    }

    throw error;
  }
};

const removeCallPlan = async (axiosInstance: AxiosInstance, organisationUnitId: string): Promise<void> => {
  await axiosInstance.delete(`${callPlansBaseUrlV2}/${CallPlanQueryPaths.RemoveCallPlan}/${organisationUnitId}`);
};

export const useReadCallPlan = (organisationUnitId: string | undefined) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [CallPlanQueryPaths.ReadCallPlan, organisationUnitId],
    queryFn: () => readCallPlan(authenticatedInstance, organisationUnitId!),
    enabled: !!organisationUnitId,
  });
};

export const useUpsertCallPlan = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: UpsertCallPlanCommand) => upsertCallPlan(authenticatedInstance, command),
    onSuccess: (_, command) => {
      queryClient.invalidateQueries({ queryKey: [CallPlanQueryPaths.ReadCallPlan, command.organisationUnitId] });
      queryClient.invalidateQueries({ queryKey: [SubscribableOrganisationUnitTreeQueryPaths.ReadSubscribableOrganisationUnitTree] });
    },
    onError: (_, command) => {
      queryClient.invalidateQueries({ queryKey: [CallPlanQueryPaths.ReadCallPlan, command.organisationUnitId] });
      queryClient.invalidateQueries({ queryKey: [SubscribableOrganisationUnitTreeQueryPaths.ReadSubscribableOrganisationUnitTree] });
    },
  });
};

export const useRemoveCallPlan = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (organisationUnitId: string) => removeCallPlan(authenticatedInstance, organisationUnitId),
    onSuccess: (_, organisationUnitId) => {
      queryClient.invalidateQueries({ queryKey: [CallPlanQueryPaths.ReadCallPlan, organisationUnitId] });
      queryClient.invalidateQueries({ queryKey: [SubscribableOrganisationUnitTreeQueryPaths.ReadSubscribableOrganisationUnitTree] });
    },
  });
};
